'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var React = _interopDefault(require('react'));
var PropTypes = _interopDefault(require('prop-types'));

var name = "@nrk/core-input";
var version = "1.1.3";

var IS_BROWSER = typeof window !== 'undefined';
var IS_ANDROID = IS_BROWSER && /(android)/i.test(navigator.userAgent); // Bad, but needed
var IS_IOS = IS_BROWSER && /iPad|iPhone|iPod/.test(String(navigator.platform));
var HAS_EVENT_OPTIONS = (function (has) {
  if ( has === void 0 ) has = false;

  try { window.addEventListener('test', null, { get passive () { has = true; } }); } catch (e) {}
  return has
})();

/**
* addEvent
* @param {String} uuid An unique ID of the event to bind - ensurnes single instance
* @param {String} type The type of event to bind
* @param {Function} handler The function to call on event
* @param {Boolean|Object} options useCapture or options object for addEventListener. Defaults to false
*/
function addEvent (uuid, type, handler, options) {
  if ( options === void 0 ) options = false;

  if (typeof window === 'undefined' || window[uuid = uuid + "-" + type]) { return } // Ensure single instance
  if (!HAS_EVENT_OPTIONS && typeof options === 'object') { options = Boolean(options.capture); } // Fix unsupported options
  var node = (type === 'resize' || type === 'load') ? window : document;
  node.addEventListener(window[uuid] = type, handler, options);
}

/**
* escapeHTML
* @param {String} str A string with potential html tokens
* @return {String} Escaped HTML string according to OWASP recommendation
*/
var ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '/': '&#x2F;', '\'': '&#x27;' };
function escapeHTML (str) {
  return String(str || '').replace(/[&<>"'/]/g, function (char) { return ESCAPE_MAP[char]; })
}

/**
* exclude
* @param {Object} target The target object
* @param {Object} exclude The source to exclude keys from
* @return {Object} The target object without keys found in source
*/
function exclude (target, exclude, include) {
  if ( include === void 0 ) include = {};

  return Object.keys(target).reduce(function (acc, key) {
    if (!exclude.hasOwnProperty(key)) { acc[key] = target[key]; }
    return acc
  }, include)
}

/**
* dispatchEvent - with infinite loop prevention
* @param {Element} elem The target object
* @param {String} name The source object(s)
* @param {Object} detail Detail object (bubbles and cancelable is set to true)
* @return {Boolean} Whether the event was canceled
*/
var IGNORE = 'prevent_recursive_dispatch_maximum_callstack';
function dispatchEvent (element, name, detail) {
  if ( detail === void 0 ) detail = {};

  var ignore = "" + IGNORE + name;
  var event;

  if (element[ignore]) { return true } // We are already processing this event, so skip sending a new one
  else { element[ignore] = true; } // Add name to dispatching ignore

  if (typeof window.CustomEvent === 'function') {
    event = new window.CustomEvent(name, { bubbles: true, cancelable: true, detail: detail });
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent(name, true, true, detail);
  }
  // IE reports incorrect event.defaultPrevented
  // but correct return value on element.dispatchEvent
  var result = element.dispatchEvent(event);
  element[ignore] = null; // Remove name from dispatching ignore

  return result // Follow W3C standard for return value
}

/**
* requestAnimFrame (super simple polyfill)
*/
function requestAnimFrame (fn) {
  (window.requestAnimationFrame || window.setTimeout)(fn);
}

/**
* queryAll
* @param {String|NodeList|Array|Element} elements A CSS selector string, nodeList, element array, or single element
* @return {Element[]} Array of elements
*/
function queryAll (elements, context) {
  if ( context === void 0 ) context = document;

  if (elements) {
    if (elements.nodeType) { return [elements] }
    if (typeof elements === 'string') { return [].slice.call(context.querySelectorAll(elements)) }
    if (elements.length) { return [].slice.call(elements) }
  }
  return []
}

var UUID = ("data-" + name + "-" + version).replace(/\W+/g, '-'); // Strip invalid attribute characters
var KEYS = { ENTER: 13, ESC: 27, PAGEUP: 33, PAGEDOWN: 34, END: 35, HOME: 36, UP: 38, DOWN: 40 };
var ITEM = '[tabindex="-1"]';
var AJAX_DEBOUNCE = 500;

function input (elements, content) {
  var options = typeof content === 'object' ? content : { content: content };
  var repaint = typeof options.content === 'string';

  return queryAll(elements).map(function (input) {
    var list = input.nextElementSibling;
    var ajax = typeof options.ajax === 'undefined' ? input.getAttribute(UUID) : options.ajax;

    input.setAttribute(UUID, ajax || '');
    input.setAttribute(IS_IOS ? 'data-role' : 'role', 'combobox'); // iOS does not inform user area is editable if combobox
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', 'off');

    if (repaint) { list.innerHTML = options.content; }
    queryAll('a,button', list).forEach(setupItem);
    setupExpand(input, options.open);
    return input
  })
}

// Expose helper functions
input.escapeHTML = escapeHTML;
input.highlight = function (haystack, needle) {
  var escapedRegExp = needle.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); // From lodash
  return escapeHTML(haystack).replace(new RegExp(escapedRegExp || '.^', 'gi'), '<mark>$&</mark>')
};

addEvent(UUID, 'click', onClickOrFocus);
addEvent(UUID, 'focus', onClickOrFocus, true); // Use focus with capturing instead of focusin for old Firefox
function onClickOrFocus (event) {
  if (event.ctrlKey || event.altKey || event.metaKey || event.defaultPrevented) { return }

  queryAll(("[" + UUID + "]")).forEach(function (input) {
    var list = input.nextElementSibling;
    var open = input === event.target || list.contains(event.target);
    var item = event.type === 'click' && open && queryAll(ITEM, list).filter(function (item) { return item.contains(event.target); })[0];

    if (item) { onSelect(input, { relatedTarget: list, currentTarget: item, value: item.value || item.textContent.trim() }); }
    else { setupExpand(input, open); }
  });
}

addEvent(UUID, 'input', function (ref) {
  var input = ref.target;

  if (input.hasAttribute(UUID)) { onFilter(input, { relatedTarget: input.nextElementSibling }); }
});

addEvent(UUID, 'keydown', function (event) {
  if (event.ctrlKey || event.altKey || event.metaKey) { return }
  if (event.target.hasAttribute(UUID)) { return onKey(event.target, event) } // Quick check
  for (var el = event.target, prev = (void 0); el; el = el.parentElement) { // Check if inside list
    if ((prev = el.previousElementSibling) && prev.hasAttribute(UUID)) { return onKey(prev, event) }
  }
}, true); // Use capture to enable checking defaultPrevented (from ESC key) in parents

function onKey (input, event) {
  var list = input.nextElementSibling;
  var focusable = [input].concat(queryAll((ITEM + ":not([hidden])"), list));
  var isClosing = event.keyCode === KEYS.ESC && input.getAttribute('aria-expanded') === 'true';
  var index = focusable.indexOf(document.activeElement);
  var item = false;

  if (event.keyCode === KEYS.DOWN) { item = focusable[index + 1] || focusable[0]; }
  else if (event.keyCode === KEYS.UP) { item = focusable[index - 1] || focusable.pop(); }
  else if (list.contains(event.target)) { // Aditional shortcuts if focus is inside list
    if (event.keyCode === KEYS.END || event.keyCode === KEYS.PAGEDOWN) { item = focusable.pop(); }
    else if (event.keyCode === KEYS.HOME || event.keyCode === KEYS.PAGEUP) { item = focusable[1]; }
    else if (event.keyCode !== KEYS.ENTER) { input.focus(); }
  }

  // Prevent leaving maximized safari and event.preventDefault even if undefined item (empty list)
  setupExpand(input, event.keyCode !== KEYS.ESC);
  if (item !== false || isClosing) { event.preventDefault(); }
  if (item) { item.focus(); }
}

function onSelect (input, detail) {
  if (dispatchEvent(input, 'input.select', detail)) {
    input.value = detail.value;
    input.focus();
    setupExpand(input, false);
  }
}

function onFilter (input, detail) {
  if (dispatchEvent(input, 'input.filter', detail) && !ajax(input)) {
    queryAll(ITEM, input.nextElementSibling).reduce(function (acc, item) {
      var list = item.parentElement.nodeName === 'LI' && item.parentElement;
      var show = item.textContent.toLowerCase().indexOf(input.value.toLowerCase()) !== -1;
      var attr = show ? 'removeAttribute' : 'setAttribute';
      if (list) { list[attr]('hidden', ''); } // JAWS requires hiding of <li> too (if they exist)
      item[attr]('hidden', '');
      return show ? acc.concat(item) : acc
    }, []).forEach(setupItem);
  }
}

function setupExpand (input, open) {
  if ( open === void 0 ) open = input.getAttribute('aria-expanded') === 'true';

  requestAnimFrame(function () { // Fixes VoiceOver Safari focus jumping to parentElement
    input.nextElementSibling[open ? 'removeAttribute' : 'setAttribute']('hidden', '');
    input.setAttribute('aria-expanded', open);
  });
}

function setupItem (item, index, items) {
  item.setAttribute('aria-label', ((item.textContent.trim()) + ", " + (index + 1) + " av " + (items.length)));
  item.setAttribute('tabindex', '-1');
  item.setAttribute('type', 'button');
}

function ajax (input) {
  var url = input.getAttribute(UUID);
  var req = ajax.req = ajax.req || new window.XMLHttpRequest();
  if (!url) { return false }

  clearTimeout(ajax.timer); // Clear previous search
  req.abort(); // Abort previous request
  req.onload = function () {
    try { req.responseJSON = JSON.parse(req.responseText); } catch (err) { req.responseJSON = false; }
    dispatchEvent(input, 'input.ajax', req);
  };
  ajax.timer = setTimeout(function () {
    if (!input.value) { return } // Abort if input is empty
    if (dispatchEvent(input, 'input.ajax.beforeSend', req)) {
      req.open('GET', url.replace('{{value}}', window.encodeURIComponent(input.value)), true);
      req.setRequestHeader('X-Requested-With', 'XMLHttpRequest'); // https://en.wikipedia.org/wiki/List_of_HTTP_header_fields#Requested-With
      req.send();
    }
  }, AJAX_DEBOUNCE); // Debounce request
}

var Input = (function (superclass) {
  function Input (props) {
    superclass.call(this, props);
    this.onFilter = this.onFilter.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onAjax = this.onAjax.bind(this);
  }

  if ( superclass ) Input.__proto__ = superclass;
  Input.prototype = Object.create( superclass && superclass.prototype );
  Input.prototype.constructor = Input;

  var staticAccessors = { defaultProps: { configurable: true } };
  staticAccessors.defaultProps.get = function () { return { open: null, ajax: null, onAjax: null, onFilter: null, onSelect: null } };

  Input.prototype.componentDidMount = function componentDidMount () { // Mount client side only to avoid rerender
    this.el.addEventListener('input.filter', this.onFilter);
    this.el.addEventListener('input.select', this.onSelect);
    this.el.addEventListener('input.ajax', this.onAjax);
    input(this.el.firstElementChild, this.props);
  };
  Input.prototype.componentDidUpdate = function componentDidUpdate () { input(this.el.firstElementChild); }; // Must mount also on update in case content changes
  Input.prototype.componentWillUnmount = function componentWillUnmount () {
    this.el.removeEventListener('input.filter', this.onFilter);
    this.el.removeEventListener('input.select', this.onSelect);
    this.el.removeEventListener('input.ajax', this.onAjax);
  };
  Input.prototype.onFilter = function onFilter (event) { this.props.onFilter && this.props.onFilter(event); };
  Input.prototype.onSelect = function onSelect (event) { this.props.onSelect && this.props.onSelect(event); };
  Input.prototype.onAjax = function onAjax (event) { this.props.onAjax && this.props.onAjax(event); };
  Input.prototype.render = function render () {
    var this$1 = this;

    return React.createElement('div', exclude(this.props, Input.defaultProps, { ref: function (el) { return (this$1.el = el); } }),
      React.Children.map(this.props.children, function (child, adjacent) {
        if (adjacent === 0) { return React.cloneElement(child, { 'aria-expanded': String(Boolean(this$1.props.open)) }) }
        if (adjacent === 1) { return React.cloneElement(child, { 'hidden': !this$1.props.open }) }
        return child
      })
    )
  };

  Object.defineProperties( Input, staticAccessors );

  return Input;
}(React.Component));

Input.Highlight = function (ref) {
    var text = ref.text;
    var query = ref.query; if ( query === void 0 ) query = '';

    return React.createElement('span', { dangerouslySetInnerHTML: {
    __html: input.highlight(text, query) // We know coreInput escapes, so this is safe
  } });
};

Input.propTypes = {
  onFilter: PropTypes.func,
  onSelect: PropTypes.func,
  onAjax: PropTypes.func,
  open: PropTypes.bool,
  ajax: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ])
};

module.exports = Input;



//////////////////
// WEBPACK FOOTER
// ./node_modules/@nrk/core-input/jsx.js
// module id = 165
// module chunks = 0